import { Select, Typography } from "antd";
import { DefaultOptionType, SelectProps } from "antd/es/select";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { MULTISELECT_ALL_VALUE } from "localConstants";

const { Text } = Typography;

// Extended value type to support boolean values (Ant Design's DefaultOptionType doesn't include boolean)
type OptionValue = string | number | boolean;

interface SelectOptionsHookResult {
    options: Array<{ label: string; value: OptionValue }>;
    loading: boolean;
    error: string | null;
}

export type SearchableSelectProps = Partial<SelectProps> & {
    hookResult: SelectOptionsHookResult;
    translationNamespace: string;
    withAllOption?: boolean;
};

export type SearchableSelectWrapperProps = Omit<SearchableSelectProps, "hookResult" | "translationNamespace">;

export const SearchableSelect = ({
    hookResult,
    translationNamespace,
    withAllOption = false,
    onChange,
    value,
    ...props
}: SearchableSelectProps) => {
    const { t } = usePaldockTranslation(translationNamespace);
    const { t: tCommon } = usePaldockTranslation("common");
    const { options, loading, error } = hookResult;

    const selected: OptionValue[] = Array.isArray(value) ? value : [];
    const isAllOptionSelected = selected.includes(MULTISELECT_ALL_VALUE);
    const isSomethingElseSelected = selected.some((val) => val !== MULTISELECT_ALL_VALUE);

    // Compute options with "All" option if enabled
    const computedOptions = withAllOption
        ? [
              {
                  label: tCommon("all"),
                  value: MULTISELECT_ALL_VALUE,
                  disabled: isSomethingElseSelected,
              },
              ...options.map((opt) => ({
                  ...opt,
                  disabled: isAllOptionSelected,
              })),
          ]
        : options;

    const handleChange = (selectedValues: OptionValue[], option?: DefaultOptionType | DefaultOptionType[]) => {
        if (!withAllOption) {
            onChange?.(selectedValues, option);
            return;
        }

        let newValue: OptionValue[];

        if (selectedValues.includes(MULTISELECT_ALL_VALUE) && selectedValues.length > 1) {
            newValue = [MULTISELECT_ALL_VALUE];
        } else if (selectedValues.length === 0) {
            newValue = [];
        } else if (selectedValues.includes(MULTISELECT_ALL_VALUE)) {
            newValue = [MULTISELECT_ALL_VALUE];
        } else {
            newValue = selectedValues.filter((val) => val !== MULTISELECT_ALL_VALUE);
        }

        onChange?.(newValue, option);
    };

    // Hide value while loading to prevent showing raw UUIDs before labels are available
    const displayValue = loading ? undefined : value;
    const placeholder = loading ? tCommon("loading") : t("placeholder");

    return (
        <>
            <Select
                showSearch
                filterOption={(input, option) =>
                    (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                placeholder={placeholder}
                options={computedOptions as DefaultOptionType[]}
                loading={loading}
                disabled={loading || !!error}
                status={error ? "error" : undefined}
                maxTagCount="responsive"
                {...props}
                onChange={handleChange as SelectProps['onChange']}
                value={displayValue}
            />
            {error && (
                <Text
                    type="danger"
                    style={{ fontSize: '13px', marginTop: '4px', display: 'block' }}
                    role="alert"
                    aria-live="polite"
                >
                    {error}
                </Text>
            )}
        </>
    );
};
